import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Snackbar, Alert } from '@mui/material';

interface MessageContextType {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const MessageContext = createContext<MessageContextType | undefined>(undefined);

interface MessageProviderProps {
  children: ReactNode;
}

export const MessageProvider: React.FC<MessageProviderProps> = ({ children }) => {
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const showSuccess = (message: string) => {
    setNotification({
      open: true,
      message,
      severity: 'success',
    });
  };

  const showError = (message: string) => {
    setNotification({
      open: true,
      message,
      severity: 'error',
    });
  };

  const showWarning = (message: string) => {
    setNotification({
      open: true,
      message,
      severity: 'warning',
    });
  };

  const showInfo = (message: string) => {
    setNotification({
      open: true,
      message,
      severity: 'info',
    });
  };

  const handleClose = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const value: MessageContextType = {
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <MessageContext.Provider value={value}>
      {children}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleClose}
        sx={{
          top: '50% !important',
          transform: 'translateY(-50%)',
        }}
      >
        <Alert
          onClose={handleClose}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </MessageContext.Provider>
  );
};

export const useMessage = (): MessageContextType => {
  const context = useContext(MessageContext);
  if (context === undefined) {
    throw new Error('useMessage must be used within a MessageProvider');
  }
  return context;
};
